import React, { useState } from "react";
import { Button } from "../ui/button";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";
import { api } from "../../lib/api";
import { apiErr, cn } from "../../lib/utils";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Lock, Info, Target } from "lucide-react";
import {
  GOAL_OPTIONS, LITE_LOCKED_GOALS, LISTED_ONLY_GOALS, CARBON_TAX_GOAL, CARBON_TAX_LOCK_REASON,
  BASELINE_GOAL, EMISSIONS_GOAL,
} from "../../lib/onboardingConfig";

export function ReportingGoalsStage({ sessionId, profile, onBack, onDone }) {
  const [selected, setSelected] = useState(profile?.reporting_goals || []);
  const [busy, setBusy] = useState(false);

  const isListed = profile?.entity_category === "SGX-Listed";
  const cpaTaxable = !!profile?.cpa_taxable;

  const lockReason = (value) => {
    if (value in LITE_LOCKED_GOALS) return LITE_LOCKED_GOALS[value];              // Lite trial locks
    if (!isListed && value in LISTED_ONLY_GOALS) return LISTED_ONLY_GOALS[value]; // non-listed locks
    if (value === CARBON_TAX_GOAL && !cpaTaxable) return CARBON_TAX_LOCK_REASON;  // carbon-tax scope
    return null;
  };

  const toggle = (value) => {
    if (lockReason(value)) return;
    setSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((x) => x !== value);
      }
      const next = [...prev, value];
      // Selecting the baseline goal auto-pairs carbon-emissions tracking.
      if (value === BASELINE_GOAL && !next.includes(EMISSIONS_GOAL) && !lockReason(EMISSIONS_GOAL)) {
        next.push(EMISSIONS_GOAL);
      }
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/trial/submit", {
        session_id: sessionId, reporting_goals: selected,
      });
      onDone(data);
    } catch (e) { toast.error(apiErr(e, "Could not submit your trial")); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="reporting-goals-stage">
      <div className="flex items-center gap-2 mb-2 text-accent"><Target className="h-4 w-4" /><span className="label-eyebrow">Reporting goals</span></div>
      <h2 className="font-display text-3xl font-bold tracking-tight">What do you want Lestari to help with?</h2>
      <p className="text-muted-foreground text-sm mt-2 max-w-lg">
        Pick your goals so we switch on the right workflows. Some options aren't available on the Lite trial or for your
        entity type — those are greyed out with a reason.
      </p>

      <TooltipProvider delayDuration={150}>
        <div className="grid sm:grid-cols-2 gap-2 mt-6" data-testid="goals-grid">
          {GOAL_OPTIONS.map((g) => {
            const locked = lockReason(g.value);
            const active = selected.includes(g.value);
            const btn = (
              <button
                key={g.value}
                type="button"
                onClick={() => toggle(g.value)}
                disabled={!!locked}
                data-testid={`goal-${g.value}`}
                className={cn(
                  "flex items-center gap-2 px-3 py-2.5 rounded-sm border text-sm text-left transition-colors w-full",
                  locked
                    ? "border-border bg-surface/40 text-muted-foreground/50 cursor-not-allowed"
                    : active
                      ? "border-accent bg-accent/10 text-foreground"
                      : "border-border hover:bg-surface-hover text-muted-foreground"
                )}
              >
                <span className={cn("h-4 w-4 rounded-sm border flex items-center justify-center shrink-0",
                  active && !locked ? "bg-accent border-accent" : "border-border")}>
                  {active && !locked && <Check className="h-3 w-3 text-black" />}
                  {locked && <Lock className="h-2.5 w-2.5" />}
                </span>
                <span className="flex-1">{g.label}</span>
                {!locked && g.tip && <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
              </button>
            );
            return locked || g.tip ? (
              <Tooltip key={g.value}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="top" data-testid={`goal-tip-${g.value}`}>{locked || g.tip}</TooltipContent>
              </Tooltip>
            ) : btn;
          })}
        </div>
      </TooltipProvider>

      <div className="text-xs text-muted-foreground font-mono mt-3">{selected.length} goal(s) selected</div>

      <div className="flex items-center gap-3 mt-7">
        <Button variant="outline" onClick={onBack} data-testid="goals-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Button variant="primary" size="lg" className="gap-2" disabled={busy} onClick={submit} data-testid="goals-submit-btn">
          {busy ? "Submitting…" : "Submit & get my welcome email"} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
